import { useCallback, useEffect, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { UserBlockingError } from '@hookmyapp/observability/errors';
import { useWorkspace } from '@/contexts/workspace-context';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Phase 131 — SSE for /deliveries/stream.
 *
 * Single EventSource per (scope, workspace) tuple. Surfaces `items` (newest
 * first), `ready` (server has acked the scope), `takenOver` (a peer connected
 * with the same key), and `reclaim()` to take over from another tab.
 *
 * Uses @microsoft/fetch-event-source so Bearer + X-Workspace-Id headers can
 * be set — native EventSource cannot.
 */
export interface DeliveryStreamItem {
  /** wd_* publicId of the row that was inserted. */
  publicId: string;
  receivedAt: string;
}

// Augment Window with the deliveries stream test seam so Playwright tests
// can emit mock SSE frames without a double-cast.
declare global {
  interface Window {
    __mockDeliveriesStreamEmit?: (event: string, payload: unknown) => void;
  }
}

interface UseDeliveriesStreamArgs {
  scope: string;
  enabled?: boolean;
}

interface State {
  items: DeliveryStreamItem[];
  ready: boolean;
  takenOver: boolean;
  error: string | null;
}

export interface UseDeliveriesStreamResult extends State {
  reclaim: () => void;
}

export function useDeliveriesStream({
  scope,
  enabled = true,
}: UseDeliveriesStreamArgs): UseDeliveriesStreamResult {
  const [state, setState] = useState<State>({
    items: [],
    ready: false,
    takenOver: false,
    error: null,
  });
  const [reconnectVersion, setReconnectVersion] = useState(0);
  const { activeWorkspace } = useWorkspace();
  const activeWorkspaceId = activeWorkspace?.id ?? null;

  const apply = useCallback((event: string, dataStr: string) => {
    if (event === 'ready') {
      setState((s) => ({ ...s, ready: true, takenOver: false, error: null }));
      return;
    }
    if (event === 'delivery') {
      try {
        const item = JSON.parse(dataStr) as DeliveryStreamItem;
        if (!item.publicId) return;
        setState((s) => {
          // Newest first; dedup by publicId so a snapshot replay doesn't
          // double-insert.
          if (s.items.some((i) => i.publicId === item.publicId)) return s;
          return { ...s, items: [item, ...s.items] };
        });
      } catch {
        /* ignore parse */
      }
      return;
    }
    if (event === 'taken_over') {
      setState((s) => ({ ...s, takenOver: true }));
      return;
    }
    if (event === 'closed') {
      // Server-initiated soft-close — let the user's reclaim() drive a fresh
      // connection on the next mount.
      return;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !scope) return;
    if (!activeWorkspaceId) return;

    const abort = new AbortController();
    window.__mockDeliveriesStreamEmit = (event, payload) => {
      apply(event, typeof payload === 'string' ? payload : JSON.stringify(payload));
    };

    void (async () => {
      try {
        await fetchEventSource(
          `${API_URL}/deliveries/stream?scope=${encodeURIComponent(scope)}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'X-Workspace-Id': activeWorkspaceId,
            },
            signal: abort.signal,
            openWhenHidden: true,
            onopen: async (resp) => {
              if (!resp.ok) {
                setState((s) => ({ ...s, error: `stream-open-${resp.status}` }));
                throw new UserBlockingError({
                  message: `Deliveries stream open failed: HTTP ${resp.status}`,
                  code: 'DELIVERIES_STREAM_OPEN_FAILED',
                  details: { status: resp.status },
                });
              }
            },
            onmessage: (msg) => {
              if (!msg.event) return;
              apply(msg.event, msg.data ?? '');
            },
            onerror: () => {
              setState((s) => ({ ...s, error: 'stream-error' }));
              // Returning nothing here lets fetchEventSource reconnect by
              // default — which is what we want; transient drops should not
              // require a manual reclaim.
            },
            onclose: () => {
              /* let onerror handle re-attach */
            },
          },
        );
      } catch {
        if (!abort.signal.aborted) {
          setState((s) => ({ ...s, error: 'stream-handshake' }));
        }
      }
    })();

    return () => {
      abort.abort();
      delete window.__mockDeliveriesStreamEmit;
    };
  }, [scope, enabled, activeWorkspaceId, reconnectVersion, apply]);

  const reclaim = useCallback(() => {
    setState((s) => ({ ...s, takenOver: false, error: null }));
    setReconnectVersion((v) => v + 1);
  }, []);

  return { ...state, reclaim };
}
